import type { Page } from '../../types.js';

/**
 * The first screen. Its job is to answer "what is this and is it for me" without
 * scrolling, then hand the reader to the page that matches their question.
 *
 * Deliberately front-loaded with `stats` and `flow`: the four numbers and the
 * five-step journey are the whole pitch, and a reader who stops here should still
 * have taken the right impression away.
 */
const page: Page = {
  slug: '',
  title: 'Gold and silver, inside your own app',
  lede:
    'Nuqi is a precious metals platform built for banks. Your customers buy, hold and sell gold and silver on your screens, under your brand. We run the pricing, the trade, the ledger and the record of who owns what.',
  group: 'Start here',
  blocks: [
    {
      kind: 'stats',
      items: [
        { value: '2', label: 'Metals', sub: 'Gold and silver, each switchable per bank', accent: 'teal' },
        { value: '6', label: 'Client platforms', sub: 'Web, widget, React Native, iOS, Android, Huawei', accent: 'blue' },
        { value: '1', label: 'Database per bank', sub: 'With an encryption key used for nothing else', accent: 'violet' },
        { value: '0', label: 'Personal data fields we receive', sub: 'You send a reference you invented, never a name', accent: 'violet' },
      ],
    },

    { kind: 'heading', text: 'What your customer does' },
    {
      kind: 'flow',
      accent: 'teal',
      steps: [
        { title: 'Opens your app', sub: 'Your login, your MFA' },
        { title: 'Sees a live rate', sub: 'Buy and sell, per gram' },
        { title: 'Enters an amount', sub: 'Cash, not grams' },
        { title: 'Confirms', sub: 'At the rate on screen' },
        { title: 'Holds, then sells', sub: 'Portfolio and history' },
      ],
    },
    {
      kind: 'prose',
      text: 'The customer never leaves your app and never creates a second account. Nothing they see carries our name unless you decide it should.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Four ways to put it in front of a customer' },
    {
      kind: 'cards',
      columns: 4,
      items: [
        {
          title: 'Widget',
          text: 'Our screens in a sandboxed frame inside your page. You place them and theme them; we build and maintain them.',
          accent: 'teal',
        },
        {
          title: 'Components',
          text: 'Our screens rendered inside your own React or React Native tree, sharing your DOM and your stylesheet.',
          accent: 'blue',
        },
        {
          title: 'SDK',
          text: 'Your screens, our logic. Prices, quotes, orders, holdings, retries and money arithmetic handled underneath.',
          accent: 'blue',
        },
        {
          title: 'API',
          text: 'Your servers call ours directly. The right shape for reconciliation, back-office tools and batch work.',
          accent: 'violet',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'The choice is per screen, not per bank',
      text: 'A widget rate panel on the home screen, a hand-built trade screen on the SDK, and your servers calling the API for the nightly reconciliation is a supported shape. See [Surfaces](/business/surfaces).',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Who owns what' },
    {
      kind: 'split',
      left: {
        title: 'You own',
        body: [
          {
            kind: 'list',
            items: [
              'The customer relationship, KYC and login.',
              'Your brand, wording and languages.',
              'Your margin, your fees and your per-customer limits.',
              'The app or website, unless you take our screens.',
              'Support for your own customers.',
            ],
          },
        ],
      },
      right: {
        title: 'We own',
        body: [
          {
            kind: 'list',
            items: [
              'The two-sided metal price and the trade behind it.',
              'The double-entry ledger and the record of who holds what.',
              'Enforcement of your limits, on every order, server-side.',
              'The screens, if you take the widget or component route.',
              'Support for your team, never for your customers.',
            ],
          },
        ],
      },
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Where to go next' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'What it is',
          text: 'The product in plain words: what a bank buys, what it configures, and what is not built yet.',
          href: '/business/what-it-is',
          accent: 'teal',
          cta: 'Read',
        },
        {
          title: 'Pricing and commission',
          text: 'Four commission legs, per metal and per currency. Where your margin sits and who can see it.',
          href: '/business/pricing-and-commission',
          accent: 'amber',
          cta: 'Read',
        },
        {
          title: 'Security and trust',
          text: 'The page to hand a compliance officer: isolation, key custody, maker-checker and audit.',
          href: '/business/security-and-trust',
          accent: 'violet',
          cta: 'Read',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Some of the catalogue is reserved rather than built',
      text: 'Price alerts, recurring savings plans, physical delivery, cash payouts, downloadable statements and the price chart are scoped and flagged but not implemented. If one is load-bearing for your business case, raise it before scope is fixed.',
    },
  ],
};

export default page;
